import Phaser from "phaser";
import { WAVES } from "../data/waves";
import { ENEMIES, scaleHp, scaleSpeed } from "../data/enemies";
import { Enemy } from "../entities/Enemy";
import type { GridPath } from "../entities/GridPath";
import type { Tower } from "../entities/Tower";
import type { WaveDef } from "../types";

interface ScheduledSpawn {
  enemyId: keyof typeof ENEMIES;
  fireAt: number;
}

export type Difficulty = "easy" | "medium" | "hard";

export interface DifficultyConfig {
  hpMul: number;
  speedMul: number;
  /** Extra boss-tier creeps appended at the END of each wave. */
  extraBoss: number;
  /** Extra titan-tier creeps appended at the END of each wave. */
  extraTitan: number;
  /** Wave numbers (1-based) where an extra mega-boss (overlord) spawns. */
  megaBossWaves: number[];
}

export const DIFFICULTY_CONFIGS: Record<Difficulty, DifficultyConfig> = {
  easy:   { hpMul: 1.0, speedMul: 1.0,  extraBoss: 0, extraTitan: 0, megaBossWaves: [] },
  medium: { hpMul: 1.4, speedMul: 1.05, extraBoss: 1, extraTitan: 0, megaBossWaves: [10, 20] },
  hard:   { hpMul: 1.9, speedMul: 1.12, extraBoss: 2, extraTitan: 1, megaBossWaves: [5, 10, 15, 20] },
};

/**
 * Owns wave progression and enemy spawning. Caller drives via update(deltaMs).
 */
export class WaveManager {
  readonly totalWaves = WAVES.length;
  /** 1-based wave number; 0 means "not started yet". */
  current = 0;
  /** Whether a wave is actively spawning. */
  spawning = false;
  /** Whether between-wave (player can build & ready up). */
  intermission = true;

  readonly enemies: Enemy[] = [];
  private schedule: ScheduledSpawn[] = [];
  private waveStartedAt = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly path: GridPath,
    private readonly onLeak: (e: Enemy) => void,
    private readonly onKill: (e: Enemy) => void,
    private readonly diff: DifficultyConfig = DIFFICULTY_CONFIGS.easy,
  ) {}

  startWave(now: number): WaveDef | null {
    if (this.current >= this.totalWaves) return null;
    const wave = WAVES[this.current];
    this.current++;
    this.intermission = false;
    this.spawning = true;
    this.waveStartedAt = now;

    this.schedule = [];
    for (const s of wave.spawns) {
      const delay = s.delay ?? 0;
      for (let i = 0; i < s.count; i++) {
        this.schedule.push({
          enemyId: s.enemy,
          fireAt: now + delay + i * s.interval,
        });
      }
    }

    // Difficulty extras — append at the END of the wave so the player
    // builds toward a known threat. Spaced 700ms apart.
    const lastFireAt = this.schedule.length
      ? Math.max(...this.schedule.map((s) => s.fireAt))
      : now;
    let tail = lastFireAt + 1200;
    for (let i = 0; i < this.diff.extraBoss; i++) {
      this.schedule.push({ enemyId: "boss", fireAt: tail });
      tail += 700;
    }
    for (let i = 0; i < this.diff.extraTitan; i++) {
      this.schedule.push({ enemyId: "titan", fireAt: tail });
      tail += 800;
    }
    if (this.diff.megaBossWaves.includes(this.current)) {
      // Overlord exists in ENEMIES (mega boss tier).
      this.schedule.push({ enemyId: "overlord", fireAt: tail + 600 });
    }

    this.schedule.sort((a, b) => a.fireAt - b.fireAt);
    return wave;
  }

  /** Update spawning + enemies. Returns true if wave finished this tick. */
  update(deltaMs: number, now: number, towers: Tower[] = []): { finished: boolean } {
    // Spawn anything due
    while (this.schedule.length > 0 && this.schedule[0].fireAt <= now) {
      const s = this.schedule.shift()!;
      const def = ENEMIES[s.enemyId];
      const hp = Math.round(scaleHp(def.hp, this.current) * this.diff.hpMul);
      const speed = scaleSpeed(def.speed, def.id, this.current) * this.diff.speedMul;
      this.enemies.push(new Enemy(this.scene, this.path, def, hp, speed));
    }

    // Update enemies. Resolve any death (kill or leak) exactly once.
    for (const e of this.enemies) {
      if (e.alive) e.update(deltaMs, now, towers);
      if (!e.alive && !e.resolved) {
        e.resolved = true;
        if (e.reachedEnd) this.onLeak(e);
        else this.onKill(e);
      }
    }

    // Garbage collect dead, resolved enemies.
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      if (!this.enemies[i].alive && this.enemies[i].resolved) this.enemies.splice(i, 1);
    }

    let finished = false;
    if (this.spawning && this.schedule.length === 0 && this.enemies.length === 0) {
      this.spawning = false;
      this.intermission = true;
      finished = true;
    }
    return { finished };
  }

  isFinalWaveCleared(): boolean {
    return this.current === this.totalWaves && !this.spawning && this.enemies.length === 0;
  }
}
